import { forwardRef, useMemo, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, IFormImperativeHandleProps } from '../../../../../../common/form';
import { TextField } from '../../../../../../common/form/text-field';
import { IPasswordStepFormProps } from './password-step-form.models';
import { Checkbox } from '../../../../../../common/form/checkbox';
import { ExternalLinks, InternalLinks } from '../../../../../../../constants';
import { useStyles } from './password-step-form.styles';
import { useReactiveVar } from '@apollo/client';
import { setIsPhoneCallConsent } from '../../../../../../../graphql/configuration/configuration.cache';

export const PasswordStepForm = forwardRef<IFormImperativeHandleProps, IPasswordStepFormProps>(
  ({ onSubmit, validationSchema, defaultValues }, ref): JSX.Element => {
    const intl = useIntl();
    const classes = useStyles();

    const resultDefaultValues = useMemo(
      () =>
        defaultValues || {
          pin: '',
          tnc: false,
          authoriseJnj: false,
          marketing: false,
        },
      [defaultValues],
    );

    const isPhoneCallConsent = useReactiveVar(setIsPhoneCallConsent);

    return (
      <Form
        ref={ref}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        defaultValues={resultDefaultValues}
        className={classes.form}
        reValidateMode="onChange"
      >
        <TextField
          name="pin"
          type="password"
          fullWidth
          autoFocus
          label={<FormattedMessage id="registration.passwordStep.passwordTextField.label" />}
        />
        <Checkbox
          name="marketing"
          label={intl.formatMessage(
            { id: 'registration.passwordStep.agreements.marketing' },
            {
              consentLink: (chunks: ReactNode) => (
                <a href={ExternalLinks.consentLink} target="_blank">
                  {chunks}
                </a>
              ),
            },
          )}
        />
        {isPhoneCallConsent && (
          <Checkbox
            name="phoneCall"
            label={intl.formatMessage(
              { id: 'registration.passwordStep.agreements.phone' },
              {
                phoneConsentLink: (chunks: ReactNode) => (
                  <a href={ExternalLinks.phoneConsentLink} target="_blank">
                    {chunks}
                  </a>
                ),
                sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
              },
            )}
          />
        )}
        <Checkbox
          name="tnc"
          label={intl.formatMessage(
            { id: 'registration.passwordStep.agreements.tnc' },
            {
              myAcuvueProgramPolicyLink: (chunks: ReactNode) => (
                <a href={intl.formatMessage({ id: 'common.myAcuvuePolicy' })} target="_blank">
                  {chunks}
                </a>
              ),
              sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
              noBreak: <>&nbsp;</>,
              ast: (chunks: ReactNode) => <span className={classes.ast}>{chunks}</span>,
            },
          )}
        />
        <Checkbox
          name="authoriseJnj"
          label={intl.formatMessage(
            { id: 'registration.passwordStep.agreements.authoriseJnj' },
            {
              privacyPolicyLink: (chunks: ReactNode) => (
                <a href={ExternalLinks.privacyPolicyLink} target="_blank">
                  {chunks}
                </a>
              ),
              cookiePolicyLink: (chunks: ReactNode) => (
                <a href={InternalLinks.cookiePage} target="_blank">
                  {chunks}
                </a>
              ),
              ast: (chunks: ReactNode) => <span className={classes.ast}>{chunks}</span>,
            },
          )}
        />
      </Form>
    );
  },
);
